import { type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from '@ab-platform/ui/toaster';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof Error && 'status' in error) {
          const status = Number((error as { status: unknown }).status);
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 1;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders — process-wide infra ONLY: TanStack Query client +
 * BrowserRouter + Toaster. Authentication is intentionally NOT wired
 * here.
 *
 * Why AuthGate moved out (PR-LP2):
 *   The set-password page (`/imposta-password`) must be reachable for
 *   anonymous users that arrived from an email link. Wiring AuthGate
 *   at this layer would gate every route, making the set-password
 *   surface unusable. The dispatcher in `App.tsx` matches public
 *   routes first and only wraps the authenticated subtree in
 *   `<AuthGate>`.
 */
export function AppProviders({ children }: AppProvidersProps): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {children}
        <Toaster />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
